import React from 'react'
import { Pressable, StyleSheet, ViewStyle, PressableProps } from 'react-native'
import { Text } from './Text'
import { colors } from './theme'

type ButtonSize = 'small' | 'medium' | 'large'
type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonWeight = 'regular' | 'medium' | 'strong'
type TextTone = 'neutral' | 'secondary' | 'tertiary' | 'critical' | 'positive'

interface ButtonProps extends Omit<PressableProps, 'style'> {
	children: string
	size?: ButtonSize
	variant?: ButtonVariant
	weight?: ButtonWeight
	icon?: React.ReactNode
	trailingIcon?: React.ReactNode
	fullWidth?: boolean
	style?: ViewStyle
	backgroundColor?: string
	disabled?: boolean
	type?: 'default' | 'link'
	tone?: TextTone
}

const sizeStyles: Record<ButtonSize, { padding: number; gap: number }> = {
	small: { padding: 8, gap: 4 },
	medium: { padding: 12, gap: 8 },
	large: { padding: 16, gap: 12 },
}

const textSizeMap: Record<ButtonSize, { level: 'small' | 'body' | 'subheadline' }> = {
	small: { level: 'small' },
	medium: { level: 'body' },
	large: { level: 'subheadline' },
}

export function Button({
	children,
	size = 'medium',
	variant = 'primary',
	weight = 'regular',
	icon,
	trailingIcon,
	fullWidth = false,
	style,
	backgroundColor,
	disabled = false,
	type = 'default',
	tone,
	...props
}: ButtonProps) {
	const getBackgroundColor = () => {
		if (disabled) return colors.gray300
		if (backgroundColor) return backgroundColor
		if (variant === 'ghost' || type === 'link') return 'transparent'
		if (variant === 'secondary') return colors.gray100
		return global.orgColor || colors.blue500 // fallback to blue if orgColor not set
	}

	const getTextColor = () => {
		if (disabled) return colors.gray500
		if (variant === 'ghost' || variant === 'secondary') return colors.gray900
		return colors.white
	}

	const getBorderColor = () => {
		if (disabled) return colors.gray300
		if (variant === 'secondary') return colors.gray300
		return 'transparent'
	}

	return (
		<Pressable
			{...props}
			disabled={disabled}
			style={[
				styles.base,
				{
					...sizeStyles[size],
					...(type === 'link' && {
						padding: 0,
					}),
					backgroundColor: getBackgroundColor(),
					borderColor: getBorderColor(),
					width: fullWidth ? '100%' : 'auto',
					opacity: disabled ? 0.6 : 1,
				},
				style,
			]}
		>
			{icon}
			<Text
				level={textSizeMap[size].level}
				weight={weight}
				tone={tone}
				style={{
					color: !tone ? getTextColor() : undefined,
					textAlign: 'center',
					textDecorationLine: type === 'link' ? 'underline' : 'none',
				}}
			>
				{children}
			</Text>
			{trailingIcon}
		</Pressable>
	)
}

const styles = StyleSheet.create({
	base: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		borderWidth: 1,
	},
})
